import React from 'react';
import * as api from '../../api';
import Icons from '../common/Icons';

export const ListPane = ({
  currentMenu,
  contacts,
  setContacts,
  rooms,
  activeRoom,
  setActiveRoom,
  activeChatContact,
  setActiveChatContact,
  selectedMail,
  setSelectedMail,
  setIsComposing,
  setComposeTo,
  setComposeSubject,
  setComposeBody,
  setComposeReplyTo,
  setMobileMenuOpen,
  activeMailsList,
  readMessageIds,
  getUnreadChatCount,
  getUnreadRoomCount,
  formatBadgeCount,
  setMailListCollapsed,
  setContactProfile,
  openContactProfile,
  vaultUnlocked,
  vaultRecords,
  selectedVaultRecord,
  setSelectedVaultRecord,
  gaiaDropInbox,
  selectedDrop,
  setSelectedDrop,
  activeIdentity,
  loadGaiaDropInbox,
  gaiaDropLoading,
  user,
  t,
  displayGaiaID,
  parseToGaiaID,
  buildInitialKeyHistory,
  triggerAlert,
  setShowCreateGroupModal,
  setShowJoinGroupModal,
  activeProfileSection,
  setActiveProfileSection,
  handleClearDirectChat,
  handleLeaveRoom,
  handleDeleteGroup,
  chatMessages,
  setChatMessages,
  fetchRooms
}) => {
  const canComposeMail = currentMenu === 'inbox' || currentMenu === 'sent' || currentMenu === 'contacts' || currentMenu === 'smtp_inbox';
  const handleStartCompose = () => {
    setIsComposing(true);
    setComposeTo('');
    setComposeSubject('');
    setComposeBody('');
    setComposeReplyTo(null);
    setSelectedMail(null);
  };

  const [pinnedChatIds, setPinnedChatIds] = React.useState(() => {
    try {
      const stored = localStorage.getItem('gaiacom_pinned_chats');
      return stored ? JSON.parse(stored) : [];
    } catch (_) {
      return [];
    }
  });

  const [hiddenChatIds, setHiddenChatIds] = React.useState(() => {
    try {
      const stored = localStorage.getItem('gaiacom_hidden_chats');
      return stored ? JSON.parse(stored) : [];
    } catch (_) {
      return [];
    }
  });

  const [contextMenu, setContextMenu] = React.useState(null);

  // Close context menu on click-away
  React.useEffect(() => {
    const handleWindowClick = () => {
      setContextMenu(null);
    };
    window.addEventListener('click', handleWindowClick);
    return () => {
      window.removeEventListener('click', handleWindowClick);
    };
  }, []);

  const persistPinned = (newPinned) => {
    setPinnedChatIds(newPinned);
    localStorage.setItem('gaiacom_pinned_chats', JSON.stringify(newPinned));
  };

  const persistHidden = (newHidden) => {
    setHiddenChatIds(newHidden);
    localStorage.setItem('gaiacom_hidden_chats', JSON.stringify(newHidden));
  };

  const togglePinChat = (id) => {
    const next = pinnedChatIds.includes(id)
      ? pinnedChatIds.filter(x => x !== id)
      : [...pinnedChatIds, id];
    persistPinned(next);
  };

  const hasMessages = (contact) => {
    if (!activeIdentity || !chatMessages) return false;
    const contactGaia = parseToGaiaID(contact.gaiaID);
    const ownGaia = parseToGaiaID(activeIdentity.GaiaID);
    return chatMessages.some(msg => {
      const senderGaia = parseToGaiaID(msg.sender);
      const recipientGaia = parseToGaiaID(msg.recipient);
      return (senderGaia === contactGaia && recipientGaia === ownGaia) ||
             (senderGaia === ownGaia && recipientGaia === contactGaia);
    });
  };

  // Unhide contacts when new incoming messages arrive
  React.useEffect(() => {
    if (!activeIdentity || !chatMessages || hiddenChatIds.length === 0) return;
    const ownGaia = parseToGaiaID(activeIdentity.GaiaID);
    let changed = false;
    const nextHidden = hiddenChatIds.filter(id => {
      const contact = contacts.find(c => c.ID === id);
      if (!contact) return true;
      const contactGaia = parseToGaiaID(contact.gaiaID);
      const hasUnreadIncoming = chatMessages.some(msg => 
        parseToGaiaID(msg.sender) === contactGaia &&
        parseToGaiaID(msg.recipient) === ownGaia &&
        !readMessageIds.has(msg.id)
      );
      if (hasUnreadIncoming) {
        changed = true;
        return false; // Remove from hidden list
      }
      return true;
    });
    if (changed) {
      persistHidden(nextHidden);
    }
  }, [chatMessages, activeIdentity, contacts, hiddenChatIds, readMessageIds, parseToGaiaID]);

  const handleContextMenu = (e, item, type) => {
    e.preventDefault();
    setContextMenu({
      mouseX: e.clientX,
      mouseY: e.clientY,
      type,
      target: item
    });
  };

  const handleDeleteDM = (contact) => {
    if (!activeIdentity) return;
    if (window.confirm(t('confirm_delete_chat_desc') || `Möchtest du den Chat mit ${contact.displayName} wirklich löschen und ausblenden?`)) {
      api.clearInboxConversation(activeIdentity.ID, { peerGaiaId: contact.gaiaID, forEveryone: false })
        .then(() => {
          const contactGaia = parseToGaiaID(contact.gaiaID);
          const ownGaia = parseToGaiaID(activeIdentity.GaiaID);
          if (setChatMessages) {
            setChatMessages(prev => prev.filter(msg => !(
              (parseToGaiaID(msg.sender) === contactGaia && parseToGaiaID(msg.recipient) === ownGaia) ||
              (parseToGaiaID(msg.sender) === ownGaia && parseToGaiaID(msg.recipient) === contactGaia)
            )));
          }
          persistHidden([...hiddenChatIds, contact.ID]);
          persistPinned(pinnedChatIds.filter(x => x !== contact.ID));
          if (activeChatContact?.ID === contact.ID) {
            setActiveChatContact(null);
          }
          triggerAlert(t('erfolg') || 'Erfolg', t('chat_deleted_hidden') || 'Chat gelöscht und ausgeblendet.');
        })
        .catch(err => {
          triggerAlert(t('fehler') || 'Fehler', err.message, 'danger');
        });
    }
  };

  const handleDeleteGroupRoom = (room) => {
    const isOwner = room.CreatedBy === activeIdentity?.ID || room.CreatedBy === user?.id;
    if (isOwner) {
      if (window.confirm(t('confirm_delete_group_desc') || `Möchtest du die Gruppe "${room.Name}" wirklich löschen? Alle Kanäle und Nachrichten werden gelöscht.`)) {
        api.deleteRoom(room.ID)
          .then(() => {
            if (setChatMessages) {
              setChatMessages(prev => prev.filter(msg => msg.roomId !== room.ID));
            }
            if (activeRoom?.ID === room.ID) {
              setActiveRoom(null);
            }
            persistPinned(pinnedChatIds.filter(x => x !== room.ID));
            if (fetchRooms) fetchRooms();
            triggerAlert(t('erfolg') || 'Erfolg', t('group_deleted') || 'Gruppe erfolgreich gelöscht.');
          })
          .catch(err => {
            triggerAlert(t('fehler') || 'Fehler', err.message, 'danger');
          });
      }
    } else {
      if (window.confirm(t('confirm_leave_group_desc') || `Möchtest du die Gruppe "${room.Name}" wirklich verlassen?`)) {
        api.leaveRoom(room.ID, activeIdentity.ID)
          .then(() => {
            if (activeRoom?.ID === room.ID) {
              setActiveRoom(null);
            }
            persistPinned(pinnedChatIds.filter(x => x !== room.ID));
            if (fetchRooms) fetchRooms();
            triggerAlert(t('erfolg') || 'Erfolg', t('group_left') || 'Gruppe erfolgreich verlassen.');
          })
          .catch(err => {
            triggerAlert(t('fehler') || 'Fehler', err.message, 'danger');
          });
      }
    }
  };

  return (
    <section className="mail-list-pane">
      <div className="pane-header">
        <div className="mobile-pane-actions">
          <button
            type="button"
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Menü öffnen"
          >
            Menu
          </button>
          {canComposeMail && (
            <button
              type="button"
              className="btn-primary mobile-compose-btn"
              onClick={handleStartCompose}
            >
              {t('mail_compose') || 'Schreiben'}
            </button>
          )}
        </div>
        <div className="pane-title-row">
          <h2>
            {currentMenu === 'inbox' && t('posteingang')}
            {currentMenu === 'smtp_inbox' && (t('smtp_inbox_title') || 'SMTP Empfang')}
            {currentMenu === 'sent' && (t('gesendet') || 'Gesendete Mails')}
            {currentMenu === 'contacts' && t('adressbuch')}
            {currentMenu === 'chat' && (t('quanten_chat') || 'Quanten-Chats')}
            {currentMenu === 'groups' && t('gruppen_chats')}
            {currentMenu === 'profile' && (t('mein_profil') || 'Dein Profil')}
            {currentMenu === 'gaiadrop' && (t('drop_title') || 'GaiaDrop Inbox')}
          </h2>
          <div className="pane-title-actions">
            {canComposeMail && (
              <button
                type="button"
                className="btn-primary desktop-compose-btn"
                onClick={handleStartCompose}
              >
                <Icons.Plus /> {t('mail_compose') || 'Schreiben'}
              </button>
            )}
            <button 
              className="btn-action collapse-toggle-btn" 
              style={{ padding: '6px 10px', fontSize: '0.8rem', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', cursor: 'pointer' }}
              onClick={() => setMailListCollapsed(true)}
              title="Liste einklappen"
            >
              &lt;
            </button>
          </div>
        </div>
      </div>

      {currentMenu === 'groups' && (
        <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border-color)', background: 'rgba(0,0,0,0.03)', display: 'flex', gap: '8px' }}>
          <button 
            type="button" 
            className="btn-primary" 
            style={{ flex: 1, padding: '8px 0', fontSize: '0.8rem', cursor: 'pointer' }}
            onClick={() => setShowCreateGroupModal(true)}
          >
            {t('gruppe_erstellen') || '+ Neue Gruppe'}
          </button>
          <button 
            type="button" 
            className="btn-secondary" 
            style={{ flex: 1, padding: '8px 0', fontSize: '0.8rem', cursor: 'pointer' }}
            onClick={() => setShowJoinGroupModal(true)}
          >
            {t('gruppe_beitreten') || 'Gruppe beitreten'}
          </button>
        </div>
      )}

      {currentMenu === 'gaiadrop' && (
        <div className="gaiadrop-list-tools">
          <div className="gaiadrop-address-card">
            <span>{t('drop_own_address') || 'Deine GaiaDrop-Adresse'}</span>
            <code>{activeIdentity ? displayGaiaID(activeIdentity.GaiaID) : 'deine_adresse'}</code>
          </div>
          <div className="gaiadrop-list-actions">
            <button
              type="button"
              className="btn-action"
              onClick={loadGaiaDropInbox}
              disabled={gaiaDropLoading}
            >
              {gaiaDropLoading ? (t('drop_btn_decrypting') || 'Laden...') : (t('drop_btn_load') || 'Inbox laden')}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                if (!activeIdentity) return;
                navigator.clipboard.writeText(displayGaiaID(activeIdentity.GaiaID));
                triggerAlert(t('kopiert') || 'Kopiert', t('drop_address_copied') || 'GaiaDrop-Adresse kopiert.');
              }}
              disabled={!activeIdentity}
            >
              {t('copy') || 'Kopieren'}
            </button>
          </div>
        </div>
      )}

      {/* Search input for starting a chat with a new contact */}
      {currentMenu === 'chat' && (
        <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border-color)', background: 'rgba(0,0,0,0.03)' }}>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const inputVal = e.target.elements.newChatGaiaId.value.trim();
            if (!inputVal) return;
            const gaiaId = parseToGaiaID(inputVal);
            try {
              const res = await api.getPublicIdentity(gaiaId);
              if (res && res.publicRecord) {
                const pubRecord = JSON.parse(res.publicRecord);
                const newContact = {
                  ID: res.id,
                  gaiaID: res.gaiaID,
                  displayName: res.displayName,
                  publicKey: pubRecord.public_keys.identity,
                  abuseScore: res.trustPassport?.abuseScore || res.abuseScore || { score: 0, escalationLevel: 0 },
                  trustPassport: res.trustPassport,
                  keyHistory: res.trustPassport?.keyHistory || buildInitialKeyHistory(pubRecord.public_keys.identity, true),
                  keyConfirmedAt: new Date().toISOString()
                };
                
                const exists = contacts.find(c => c.gaiaID === res.gaiaID);
                if (!exists) {
                  const updated = [...contacts, newContact];
                  setContacts(updated);
                  localStorage.setItem(`contacts_${user.id}`, JSON.stringify(updated));
                }
                
                setActiveChatContact(newContact);
                e.target.reset();
              } else {
                triggerAlert('Nicht gefunden', 'Diese Gaia-Adresse konnte nicht im föderierten Netz gefunden werden.', 'danger');
              }
            } catch (err) {
              triggerAlert('Fehler', err.message, 'danger');
            }
          }}>
            <input 
              name="newChatGaiaId" 
              type="text" 
              placeholder={t('suchen') || 'Neue Gaia-Adresse suchen...'} 
              className="input-field" 
              style={{ padding: '8px 12px', fontSize: '0.8rem', width: '100%' }}
            />
          </form>
        </div>
      )}

      <div className="list-scroll">
        {currentMenu === 'contacts' ? (
          contacts.map(c => (
            <div 
              key={c.ID} 
              className="mail-card"
              onClick={() => {
                setIsComposing(true);
                setComposeTo(displayGaiaID(c.gaiaID));
                setComposeSubject('');
                setComposeBody('');
                setComposeReplyTo(null);
                setSelectedMail(null);
              }}
            >
              <div className="mail-card-header">
                <div className="mail-sender">{c.displayName}</div>
                <button
                  type="button"
                  className="btn-action"
                  onClick={event => {
                    event.stopPropagation();
                    openContactProfile(c.gaiaID);
                  }}
                >
                  {t('profil') || 'Profil'}
                </button>
                {c.abuseScore && c.abuseScore.score > 0 && (
                  <span className="abuse-badge" style={{ color: 'var(--warning)' }}>Score: {c.abuseScore.score}</span>
                )}
              </div>
              <div className="mail-subject" style={{ color: 'var(--text-secondary)' }}>{displayGaiaID(c.gaiaID)}</div>
              <div className="mail-preview">{t('click_to_write_mail') || 'Klicken zum Schreiben einer Mail'}</div>
            </div>
          ))
        ) : currentMenu === 'chat' ? (
          (() => {
            const displayed = contacts.filter(c => 
              pinnedChatIds.includes(c.ID) || 
              (activeChatContact && activeChatContact.ID === c.ID) || 
              (hasMessages(c) && !hiddenChatIds.includes(c.ID))
            ).sort((a, b) => {
              const aPinned = pinnedChatIds.includes(a.ID) ? 1 : 0;
              const bPinned = pinnedChatIds.includes(b.ID) ? 1 : 0;
              return bPinned - aPinned;
            });
            if (displayed.length === 0) {
              return (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  {t('no_active_chats') || 'Keine aktiven Chats. Suche oben nach Kontakten, um einen Chat zu starten.'}
                </div>
              );
            }
            return displayed.map(c => {
              const isActive = activeChatContact && activeChatContact.ID === c.ID;
              const unreadCount = getUnreadChatCount(c);
              const isPinned = pinnedChatIds.includes(c.ID);
              return (
                <div 
                  key={c.ID} 
                  className={`mail-card ${isActive ? 'active' : ''} ${isPinned ? 'pinned' : ''}`}
                  onClick={() => {
                    setActiveChatContact(c);
                  }}
                  onContextMenu={(e) => handleContextMenu(e, c, 'chat')}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="mail-sender" style={{ fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {c.displayName}
                      {isPinned && <span className="pin-indicator" title={t('pinned') || 'Angepinnt'} style={{ fontSize: '0.8rem', color: 'var(--accent-cyan)' }}>📌</span>}
                    </div>
                    <div className="mail-subject" style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>{displayGaiaID(c.gaiaID)}</div>
                    <div className="mail-preview" style={{ marginTop: '4px' }}>{t('e2e_chat_room_open') || 'E2E Chat-Raum öffnen'}</div>
                  </div>
                  {unreadCount > 0 && (
                    <span className="card-unread-badge">{formatBadgeCount(unreadCount)}</span>
                  )}
                </div>
              );
            });
          })()
        ) : currentMenu === 'groups' ? (
          (() => {
            const displayed = rooms.filter(r => 
              pinnedChatIds.includes(r.ID) || 
              (activeRoom && activeRoom.ID === r.ID) || 
              (getUnreadRoomCount(r) > 0) ||
              !hiddenChatIds.includes(r.ID)
            ).sort((a, b) => {
              const aPinned = pinnedChatIds.includes(a.ID) ? 1 : 0;
              const bPinned = pinnedChatIds.includes(b.ID) ? 1 : 0;
              return bPinned - aPinned;
            });
            if (displayed.length === 0) {
              return (
                <div className="empty-action-card">
                  <strong>{t('groups_empty_title') || 'Noch keine Gruppen-Chats'}</strong>
                  <span>{t('groups_empty_desc') || 'Erstelle einen sicheren Arbeitsraum fuer Projekt-, Krisen- oder Teamkommunikation.'}</span>
                  <div>
                    <button type="button" className="btn-primary" onClick={() => setShowCreateGroupModal(true)}>
                      {t('gruppe_erstellen') || '+ Neue Gruppe'}
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setShowJoinGroupModal(true)}>
                      {t('gruppe_beitreten') || 'Gruppe beitreten'}
                    </button>
                  </div>
                </div>
              );
            }
            return displayed.map(r => {
              const isActive = activeRoom && activeRoom.ID === r.ID;
              const unreadCount = getUnreadRoomCount(r);
              const isPinned = pinnedChatIds.includes(r.ID);
              return (
                <div 
                  key={r.ID} 
                  className={`mail-card ${isActive ? 'active' : ''} ${isPinned ? 'pinned' : ''}`}
                  onClick={() => {
                    setActiveRoom(r);
                    setSelectedMail(null);
                    setIsComposing(false);
                  }}
                  onContextMenu={(e) => handleContextMenu(e, r, 'groups')}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="mail-card-header" style={{ marginBottom: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '1.2rem' }}>{r.Avatar || 'G'}</span>
                        <span className="mail-sender" style={{ fontSize: '0.9rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {r.Name}
                          {isPinned && <span className="pin-indicator" title={t('pinned') || 'Angepinnt'} style={{ fontSize: '0.8rem', color: 'var(--accent-cyan)' }}>📌</span>}
                        </span>
                      </div>
                    </div>
                    <div className="mail-subject" style={{ color: 'var(--text-secondary)', marginTop: '4px', fontSize: '0.8rem' }}>
                      {r.Description || t('no_description') || 'Keine Beschreibung'}
                    </div>
                    <div className="mail-preview" style={{ marginTop: '4px', fontSize: '0.75rem' }}>
                      {r.Members ? `${r.Members.length} ${t('mitglieder') || 'Mitglieder'}` : `0 ${t('mitglieder') || 'Mitglieder'}`}
                    </div>
                  </div>
                  {unreadCount > 0 && (
                    <span className="card-unread-badge">{formatBadgeCount(unreadCount)}</span>
                  )}
                </div>
              );
            });
          })()
        ) : currentMenu === 'profile' ? (
          <div className="settings-menu-list" style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div 
              className={`mail-card ${activeProfileSection === 'edit' ? 'active' : ''}`}
              onClick={() => {
                setActiveProfileSection('edit');
                setMobileMenuOpen(false);
              }}
              style={{ padding: '16px 20px', cursor: 'pointer', borderRadius: '8px', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.2s' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                <span style={{ fontSize: '1.25rem' }}>👤</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mail-sender" style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                    {t('edit_profile') || 'Profil bearbeiten'}
                  </div>
                  <div className="mail-preview" style={{ marginTop: '4px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {t('edit_profile_desc') || 'Anzeigename, Biografie und Avatar anpassen.'}
                  </div>
                </div>
              </div>
            </div>

            <div 
              className={`mail-card ${activeProfileSection === 'password' ? 'active' : ''}`}
              onClick={() => {
                setActiveProfileSection('password');
                setMobileMenuOpen(false);
              }}
              style={{ padding: '16px 20px', cursor: 'pointer', borderRadius: '8px', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.2s' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                <span style={{ fontSize: '1.25rem' }}>🔒</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mail-sender" style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                    {t('change_password_title') || 'Passwort ändern'}
                  </div>
                  <div className="mail-preview" style={{ marginTop: '4px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {t('change_password_menu_desc') || 'GaiaCOM Login-Passwort aktualisieren.'}
                  </div>
                </div>
              </div>
            </div>

            <div 
              className={`mail-card ${activeProfileSection === 'devices' ? 'active' : ''}`}
              onClick={() => {
                setActiveProfileSection('devices');
                setMobileMenuOpen(false);
              }}
              style={{ padding: '16px 20px', cursor: 'pointer', borderRadius: '8px', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.2s' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                <span style={{ fontSize: '1.25rem' }}>💻</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mail-sender" style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                    {t('device_sessions_title') || 'Angemeldete Geräte'}
                  </div>
                  <div className="mail-preview" style={{ marginTop: '4px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {t('device_sessions_menu_desc') || 'Aktive Sitzungen verwalten und beenden.'}
                  </div>
                </div>
              </div>
            </div>

            <div 
              className={`mail-card ${activeProfileSection === 'keys' ? 'active' : ''}`}
              onClick={() => {
                setActiveProfileSection('keys');
                setMobileMenuOpen(false);
              }}
              style={{ padding: '16px 20px', cursor: 'pointer', borderRadius: '8px', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.2s' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                <span style={{ fontSize: '1.25rem' }}>🔑</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mail-sender" style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                    {t('kryptographische_schluessel') || 'Kryptographische Schlüssel'}
                  </div>
                  <div className="mail-preview" style={{ marginTop: '4px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {t('kryptographische_schluessel_desc') || 'Private Schlüssel und Backup Seed Phrase anzeigen.'}
                  </div>
                </div>
              </div>
            </div>

            <div 
              className={`mail-card ${activeProfileSection === 'security' ? 'active' : ''}`}
              onClick={() => {
                setActiveProfileSection('security');
                setMobileMenuOpen(false);
              }}
              style={{ padding: '16px 20px', cursor: 'pointer', borderRadius: '8px', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.2s' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                <span style={{ fontSize: '1.25rem' }}>⏱</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mail-sender" style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                    {t('crypto_lock_title') || 'Kryptografische Sperre'}
                  </div>
                  <div className="mail-preview" style={{ marginTop: '4px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {t('crypto_lock_menu_desc') || 'Reload- und Inaktivitäts-Timer steuern.'}
                  </div>
                </div>
              </div>
            </div>

            <div 
              className={`mail-card ${activeProfileSection === 'legal' ? 'active' : ''}`}
              onClick={() => {
                setActiveProfileSection('legal');
                setMobileMenuOpen(false);
              }}
              style={{ padding: '16px 20px', cursor: 'pointer', borderRadius: '8px', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.2s' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                <span style={{ fontSize: '1.25rem' }}>§</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mail-sender" style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                    {t('privacy_imprint_title') || 'Datenschutz & Impressum'}
                  </div>
                  <div className="mail-preview" style={{ marginTop: '4px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {t('privacy_imprint_menu_desc') || 'DSGVO, Datenschutz und Impressum anzeigen.'}
                  </div>
                </div>
              </div>
            </div>

            <div 
              className={`mail-card danger-menu-card ${activeProfileSection === 'danger' ? 'active' : ''}`}
              onClick={() => {
                setActiveProfileSection('danger');
                setMobileMenuOpen(false);
              }}
              style={{ padding: '16px 20px', cursor: 'pointer', borderRadius: '8px', border: '1px solid var(--danger)', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.2s' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                <span style={{ fontSize: '1.25rem' }}>!</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mail-sender" style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                    {t('delete_account_title') || 'Account löschen'}
                  </div>
                  <div className="mail-preview" style={{ marginTop: '4px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {t('delete_account_menu_desc') || 'Konto und zuordenbare Daten vernichten.'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : currentMenu === 'vault' ? (
          !vaultUnlocked ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: '40px', fontSize: '0.85rem' }}>
              Tresor ist gesperrt. Bitte rechts entsperren.
            </div>
          ) : vaultRecords.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: '40px', fontSize: '0.85rem' }}>
              {t('vault_empty') || 'Keine Records gespeichert.'}
            </div>
          ) : (
            vaultRecords.map(rec => (
              <div 
                key={rec.id} 
                className={`mail-card ${selectedVaultRecord?.id === rec.id ? 'active' : ''}`}
                onClick={() => setSelectedVaultRecord(rec)}
                style={{ padding: '12px 24px' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--accent-cyan)', marginBottom: '4px' }}>
                  <span style={{ fontWeight: 'bold' }}>{rec.category.toUpperCase()}</span>
                  <span>{new Date(rec.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="mail-sender" style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>{rec.title}</div>
                <div className="mail-preview" style={{ marginTop: '4px', fontSize: '0.75rem' }}>
                  {rec.body && rec.body.length > 60 ? rec.body.substring(0, 60) + '...' : rec.body}
                </div>
              </div>
            ))
          )
        ) : currentMenu === 'gaiadrop' ? (
          gaiaDropInbox.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: '40px', fontSize: '0.85rem' }}>
              {t('drop_empty') || 'Keine Drops geladen.'}
            </div>
          ) : (
            gaiaDropInbox.map(drop => (
              <div 
                key={drop.id} 
                className={`mail-card ${selectedDrop?.id === drop.id ? 'active' : ''} ${drop.status === 'new' ? 'unread' : ''}`}
                onClick={() => setSelectedDrop(drop)}
                style={{ padding: '12px 24px', borderLeft: drop.status === 'new' ? '3px solid var(--warning)' : 'none' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--warning)', marginBottom: '4px' }}>
                  <span style={{ fontWeight: 'bold' }}>DROP PROOF</span>
                  <span>{drop.created_at ? new Date(drop.created_at).toLocaleDateString() : 'Unbekannt'}</span>
                </div>
                <div className="mail-sender" style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                  {drop.sender_label || t('anonymous_sender') || 'Anonymer Absender'}
                </div>
                <div className="mail-preview" style={{ marginTop: '4px', fontSize: '0.75rem' }}>
                  {(() => {
                    const rawText = drop.decrypted ? (typeof drop.decrypted === 'string' ? drop.decrypted : drop.decrypted.body) : (drop.decryptError || 'Entschlüsselung ausstehend...');
                    return rawText && rawText.length > 60 ? rawText.substring(0, 60) + '...' : rawText;
                  })()}
                </div>
              </div>
            ))
          )
        ) : (
          activeMailsList.map(mail => {
            const isActive = selectedMail && selectedMail.id === mail.id;
            const isUnread = (currentMenu === 'inbox' || currentMenu === 'smtp_inbox') && !readMessageIds.has(mail.id);
            
            return (
              <div 
                key={mail.id} 
                className={`mail-card ${isActive ? 'active' : ''} ${mail.untrusted ? 'untrusted' : ''} ${isUnread ? 'unread' : ''}`}
                onClick={() => {
                  setSelectedMail(mail);
                  setIsComposing(false);
                }}
                style={isUnread ? { borderLeft: '3px solid var(--accent-cyan)' } : {}}
              >
                <div className="mail-card-header">
                  <div className="mail-sender" style={isUnread ? { fontWeight: 800 } : {}}>
                    {(currentMenu === 'inbox' || currentMenu === 'smtp_inbox') ? mail.senderGaia : mail.recipientGaia}
                    {isUnread && <span className="card-unread-dot" style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: 'var(--accent-cyan)', marginLeft: '6px' }} />}
                  </div>
                  <div className="mail-time">
                    {new Date(mail.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="mail-subject" style={isUnread ? { fontWeight: 800, color: 'var(--text-primary)' } : {}}>{mail.subject}</div>
                <div className="mail-preview">{mail.body}</div>
                {mail.untrusted && (
                  <div style={{ color: 'var(--danger)', fontSize: '0.7rem', fontWeight: 'bold', marginTop: '6px' }}>
                    ! {mail.isSmtp ? (t('smtp_legacy_badge') || 'Legacy SMTP / Unsicher') : (t('spam_verdacht') || 'Spam/Verdacht')}
                  </div>
                )}
              </div>
            );
          })
        )}

        {currentMenu !== 'contacts' && currentMenu !== 'chat' && currentMenu !== 'profile' && currentMenu !== 'groups' && currentMenu !== 'vault' && currentMenu !== 'gaiadrop' && activeMailsList.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: '40px', fontSize: '0.85rem' }}>
            {t('keine_mails') || 'Keine Mails vorhanden.'}
          </div>
        )}
      </div>
      {contextMenu && (
        <div 
          className="custom-context-menu" 
          style={{
            position: 'fixed',
            top: `${contextMenu.mouseY}px`,
            left: `${contextMenu.mouseX}px`,
            zIndex: 1000,
            background: 'var(--card-bg, #1a1a1c)',
            border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
            padding: '4px 0',
            minWidth: '140px'
          }}
        >
          <button 
            type="button"
            className="context-menu-item"
            style={{
              display: 'block',
              width: '100%',
              padding: '8px 12px',
              textAlign: 'left',
              background: 'transparent',
              border: 'none',
              color: 'var(--text-primary, #ffffff)',
              fontSize: '0.85rem',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onClick={() => {
              togglePinChat(contextMenu.target.ID);
              setContextMenu(null);
            }}
            onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.08)'}
            onMouseLeave={e => e.target.style.background = 'transparent'}
          >
            {pinnedChatIds.includes(contextMenu.target.ID) 
              ? (t('entpinnen') || 'Entpinnen') 
              : (t('anpinnen') || 'Anpinnen')}
          </button>
          <button 
            type="button"
            className="context-menu-item"
            style={{
              display: 'block',
              width: '100%',
              padding: '8px 12px',
              textAlign: 'left',
              background: 'transparent',
              border: 'none',
              color: 'var(--warning, #ff3b30)',
              fontSize: '0.85rem',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onClick={() => {
              if (contextMenu.type === 'chat') {
                handleDeleteDM(contextMenu.target);
              } else {
                handleDeleteGroupRoom(contextMenu.target);
              }
              setContextMenu(null);
            }}
            onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.08)'}
            onMouseLeave={e => e.target.style.background = 'transparent'}
          >
            {contextMenu.type === 'chat' 
              ? (t('chat_loeschen') || 'Chat löschen') 
              : (contextMenu.target.CreatedBy === activeIdentity?.ID || contextMenu.target.CreatedBy === user?.id
                  ? (t('gruppe_loeschen') || 'Gruppe löschen')
                  : (t('gruppe_verlassen') || 'Gruppe verlassen'))}
          </button>
        </div>
      )}
    </section>
  );
};

export default ListPane;
